import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface SmsSablon {
    sablonID: number;
    sablonAdi: string;
    sablonIcerigi: string;
    standart: boolean;
    sablonKodu?: string;
}

const PARAMETRELER = [
    { parametre: '[MUSTERIADI]', aciklama: 'Müşterinin adı ve soyadı' },
    { parametre: '[PROJEADI]', aciklama: 'Projenin adı' },
    { parametre: '[VADETARIHI]', aciklama: 'Ödemenin vade tarihi' },
    { parametre: '[BORCMIKTARI]', aciklama: 'Borç tutarı (TL)' },
    { parametre: '[BUGUN]', aciklama: 'Bugünün tarihi' },
    { parametre: '[DAIRENO]', aciklama: 'Daire numarası' },
    { parametre: '[6HANELIONAYKODU]', aciklama: 'Otomatik üretilen 6 haneli onay kodu' },
];

const SmsSablonlariPage: React.FC = () => {
    const { t } = useTranslation();
    const [sablonlar, setSablonlar] = useState<SmsSablon[]>([]);
    const [loading, setLoading] = useState(true);
    const [showKilavuz, setShowKilavuz] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [duzenle, setDuzenle] = useState<SmsSablon | null>(null);
    const [form, setForm] = useState({ sablonAdi: '', sablonIcerigi: '' });
    const [saving, setSaving] = useState(false);
    const [silOnay, setSilOnay] = useState<number | null>(null);

    const fetchSablonlar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/SmsSablonlari');
            setSablonlar(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSablonlar(); }, [fetchSablonlar]);

    const acYeniForm = () => {
        setDuzenle(null);
        setForm({ sablonAdi: '', sablonIcerigi: '' });
        setShowForm(true);
    };

    const acDuzenleForm = (sablon: SmsSablon) => {
        setDuzenle(sablon);
        setForm({ sablonAdi: sablon.sablonAdi, sablonIcerigi: sablon.sablonIcerigi });
        setShowForm(true);
    };

    const handleKaydet = async () => {
        if (!form.sablonAdi.trim() || !form.sablonIcerigi.trim()) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        setSaving(true);
        try {
            if (duzenle) {
                await axiosInstance.put(`/api/SmsSablonlari/${duzenle.sablonID}`, form);
                toast.success(t('msg.islemBasarili'));
            } else {
                await axiosInstance.post('/api/SmsSablonlari', form);
                toast.success(t('msg.islemBasarili'));
            }
            setShowForm(false);
            fetchSablonlar();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleSil = async (id: number) => {
        try {
            await axiosInstance.delete(`/api/SmsSablonlari/${id}`);
            toast.success(t('msg.islemBasarili'));
            setSilOnay(null);
            fetchSablonlar();
        } catch (err: any) {
            toast.error(err?.response?.data  || t('msg.islemHatasi'));
            setSilOnay(null);
        }
    };

    const insertParametre = (parametre: string) => {
        setForm(f => ({ ...f, sablonIcerigi: f.sablonIcerigi + parametre }));
    };

    return (
        <div style={{ padding: '0 0 32px' }}>
            {/* Başlık */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: '#1e293b' }}>SMS Şablonları</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Standart şablonlar düzenlenebilir ancak silinemez. Özel şablonlar ekleyebilirsiniz.</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => setShowKilavuz(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 500 }}
                    >
                        <i className="fas fa-book-open" style={{ fontSize: 12 }} />
                        Hazırlama Kılavuzu
                    </button>
                    <button
                        onClick={acYeniForm}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                    >
                        <i className="fas fa-plus" style={{ fontSize: 12 }} />
                        Yeni Şablon
                    </button>
                </div>
            </div>

            {/* Liste */}
            {loading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: 22 }} />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sablonlar.map(sablon => (
                        <div
                            key={sablon.sablonID}
                            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{sablon.sablonAdi}</span>
                                        {sablon.standart && (
                                            <span style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                                                Standart
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, background: '#f8fafc', borderRadius: 7, padding: '8px 12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {sablon.sablonIcerigi}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <button
                                        onClick={() => acDuzenleForm(sablon)}
                                        title={t('btn.duzenle')}
                                        style={{ width: 32, height: 32, borderRadius: 7, background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}
                                    >
                                        <i className="fas fa-pen" />
                                    </button>
                                    {!sablon.standart && (
                                        <button
                                            onClick={() => setSilOnay(sablon.sablonID)}
                                            title={t('btn.sil')}
                                            style={{ width: 32, height: 32, borderRadius: 7, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}
                                        >
                                            <i className="fas fa-trash" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Hazırlama Kılavuzu Modal */}
            {showKilavuz && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', width: '100%', maxWidth: 520, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="fas fa-book-open" style={{ color: '#2563eb' }} />
                                SMS Şablon Hazırlama Kılavuzu
                            </h4>
                            <button onClick={() => setShowKilavuz(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                            <p style={{ margin: '0 0 14px', fontSize: 13.5, color: '#475569', lineHeight: 1.6 }}>
                                SMS şablonlarında aşağıdaki parametreleri kullanabilirsiniz. Sistem, mesaj gönderimi sırasında bu parametreleri ilgili kişi ve işleme ait gerçek bilgilerle otomatik olarak değiştirir.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {PARAMETRELER.map(p => (
                                    <div key={p.parametre} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '10px 14px' }}>
                                        <code style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 6, padding: '3px 9px', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{p.parametre}</code>
                                        <span style={{ fontSize: 13, color: '#475569' }}>{p.aciklama}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 16, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 9, padding: '10px 14px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 4 }}>
                                    <i className="fas fa-lightbulb" style={{ marginRight: 6 }} />Örnek Kullanım
                                </div>
                                <code style={{ fontSize: 12.5, color: '#78350f', lineHeight: 1.7, display: 'block' }}>
                                    Sayın [MUSTERIADI], [PROJEADI] projesindeki<br />
                                    [DAIRENO] no'lu dairenize ait [VADETARIHI] vadeli<br />
                                    [BORCMIKTARI] tutarındaki ödemeniz yaklaşmaktadır.
                                </code>
                            </div>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowKilavuz(false)} style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, cursor: 'pointer', fontWeight: 600 }}>
                                Anladım
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ekle / Düzenle Modal */}
            {showForm && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                                {duzenle ? 'Şablonu Düzenle' : 'Yeni Şablon Ekle'}
                            </h4>
                            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Şablon Adı *</label>
                                <input
                                    type="text"
                                    value={form.sablonAdi}
                                    onChange={e => setForm(f => ({ ...f, sablonAdi: e.target.value }))}
                                    placeholder={t('label.ara')}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Şablon İçeriği *</label>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{form.sablonIcerigi.length} karakter</span>
                                </div>
                                <textarea
                                    value={form.sablonIcerigi}
                                    onChange={e => setForm(f => ({ ...f, sablonIcerigi: e.target.value }))}
                                    placeholder={t('label.ara')}
                                    rows={5}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5, outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }}
                                />
                                {/* Parametre hızlı ekle butonları */}
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 500 }}>Parametre Ekle:</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                        {PARAMETRELER.map(p => (
                                            <button
                                                key={p.parametre}
                                                type="button"
                                                onClick={() => insertParametre(p.parametre)}
                                                title={p.aciklama}
                                                style={{ padding: '3px 9px', background: '#e0e7ff', color: '#3730a3', border: 'none', borderRadius: 5, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                                            >
                                                {p.parametre}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setShowForm(false)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>{t('btn.iptal')}</button>
                            <button onClick={handleKaydet} disabled={saving} style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.75 : 1 }}>
                                {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Silme Onayı */}
            {silOnay !== null && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 20px', maxWidth: 360, width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
                        <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 16 }}>Şablonu Sil</h4>
                        <p style={{ margin: '0 0 20px', fontSize: 13.5, color: '#64748b' }}>Bu şablon kalıcı olarak silinecek. Emin misiniz?</p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            <button onClick={() => setSilOnay(null)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>{t('btn.iptal')}</button>
                            <button onClick={() => handleSil(silOnay)} style={{ padding: '8px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>{t('btn.sil')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SmsSablonlariPage;
